import { useEffect, useState } from 'react';
import { fetchAgencies } from './api';
import { DOWN_TASK_TARGET_KINDS, type AgencyView } from '@tsr/shared-types';

/**
 * Agency picker for the OUTBOUND "Task agency" controls (#379, Coordination Re-Arch
 * Phase 3b-2a). Mirrors DestinationPicker, with one deliberate difference: it does NOT
 * auto-select a default. The whole point of #379 is to replace the forward-to-SELF
 * default (the PEOC duty officer / responding agency) with a CONSCIOUS pick of a NAMED,
 * NON-SELF agency — so the operator must choose. The server's GET /api/agencies already
 * excludes the caller's own agency, so every option here is a valid non-self target.
 */

const KIND_LABEL: Record<string, string> = {
  federal: 'Federal',
  provincial: 'Provincial',
  municipal: 'Municipal',
  // First Nations are first-class down-task counterparties (W4d/#519) — never a raw enum.
  first_nation: 'First Nation',
  agency: 'Agency',
  'drone-operator': 'Drone Operator',
};

interface Props {
  /** Currently selected target agency id (undefined = nothing picked yet). */
  value: string | undefined;
  onChange: (agencyId: string | undefined) => void;
  disabled?: boolean;
  /** testid for the <select>. */
  testid?: string;
  /** Leading label. Defaults to 'Task agency'. */
  label?: string;
  /** Shown when no taskable agency exists/the registry is unreachable. */
  emptyHint?: string;
}

export function AgencyPicker({ value, onChange, disabled, testid, label, emptyHint }: Props) {
  const [agencies, setAgencies] = useState<AgencyView[]>([]);

  useEffect(() => {
    const ctrl = new AbortController();
    // Scope to DOWN-TASK target kinds (W4d/#519 §D G-3): drone-operator orgs are reached via
    // the drone marketplace, not agency_tasks, so they must not appear as a "Task agency".
    void fetchAgencies(ctrl.signal, { kinds: DOWN_TASK_TARGET_KINDS })
      .then((list) => setAgencies(list))
      .catch(() => {
        /* registry unreachable → leave empty; the empty-state hint shows */
      });
    return () => ctrl.abort();
  }, []);

  if (agencies.length === 0) {
    return emptyHint ? (
      <span
        className="text-[11px] text-amber-300/80"
        data-testid={testid ? `${testid}-empty` : undefined}
      >
        {emptyHint}
      </span>
    ) : (
      <span
        className="text-[11px] text-slate-500"
        data-testid={testid ? `${testid}-empty` : undefined}
      >
        No other agency to task.
      </span>
    );
  }

  return (
    <label className="flex items-center gap-1.5 text-[11px] text-slate-400">
      <span className="uppercase tracking-wide">{label ?? 'Task agency'}</span>
      <select
        data-testid={testid ?? 'agency-picker'}
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value || undefined)}
        className="text-xs py-1 px-2 rounded bg-slate-800 border border-slate-600 text-slate-100 disabled:opacity-50"
      >
        {/* No auto-select: the operator must consciously pick a non-self target. */}
        <option value="">Select an agency…</option>
        {agencies.map((a) => (
          <option key={a.id} value={a.id}>
            {(KIND_LABEL[a.kind] ?? a.kind) + ' — ' + a.name}
          </option>
        ))}
      </select>
    </label>
  );
}
